import type { Ad } from "@models/Ad.model";

/** Walk an ad tree and collect all leaf nodes (non-container types). */
export function collectLeafAds(ad: Ad): Ad[] {
    if (ad.type !== "container") return [ad];
    return (ad.children ?? []).flatMap(collectLeafAds);
}

function extFromContentType(ct?: string | null): string | null {
    if (!ct) return null;
    const map: Record<string, string> = {
        "video/mp4": ".mp4",
        "video/quicktime": ".mov",
        "video/webm": ".webm",
        "image/jpeg": ".jpg",
        "image/png": ".png",
        "image/webp": ".webp",
        "image/gif": ".gif",
        "audio/mpeg": ".mp3",
        "audio/wav": ".wav",
        "application/pdf": ".pdf",
    };
    const base = ct.split(";")[0].trim().toLowerCase();
    return map[base] ?? null;
}

function filenameFromContentDisposition(cd?: string | null): string | null {
    if (!cd) return null;
    try {
        const parts = cd.split(";").map((s) => s.trim());
        const fstar = parts.find((p) => p.toLowerCase().startsWith("filename*="));
        if (fstar) {
            const v = fstar.split("=")[1];
            const idx = v.indexOf("''");
            const encoded = idx >= 0 ? v.slice(idx + 2) : v;
            return decodeURIComponent(encoded.replace(/^"|"$/g, ""));
        }
        const fn = parts.find((p) => p.toLowerCase().startsWith("filename="));
        if (fn) return fn.split("=")[1].replace(/^"|"$/g, "");
    } catch {
        /* noop */
    }
    return null;
}

function hasExt(name: string): boolean {
    const base = name.split("/").pop() || name;
    return /\.[A-Za-z0-9]{1,6}$/.test(base);
}

function sanitizeName(name: string): string {
    // Control characters are exactly what this is meant to strip out of filenames.
    // eslint-disable-next-line no-control-regex
    return name.replace(/[\\/:*?"<>|\x00-\x1F]/g, "_");
}

export async function zipAndDownload(
    files: Array<{ url: string; name?: string }>,
    zipBaseName: string,
    extraTextFiles?: Record<string, string>,
): Promise<void> {
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    const results = await Promise.allSettled(
        files.map(async ({ url, name }, i) => {
            const u = new URL(url);
            const pathName = decodeURIComponent(u.pathname);
            const urlBaseName = (pathName.split("/").pop() || `file-${i + 1}`).replace(/\s+/g, " ");

            const res = await fetch(url, {
                method: "GET",
                mode: "cors",
                credentials: "omit",
                cache: "no-store",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);

            const cd = res.headers.get("Content-Disposition");
            const ct = res.headers.get("Content-Type");
            const fromCD = filenameFromContentDisposition(cd);
            const preferred = name || fromCD || urlBaseName;
            let finalName = sanitizeName(preferred);

            if (!hasExt(finalName)) {
                const ext =
                    extFromContentType(ct) ||
                    (hasExt(urlBaseName) ? urlBaseName.match(/\.[^./]+$/)![0] : "");
                finalName = sanitizeName(finalName + (ext || ""));
            }

            const buf = await res.arrayBuffer();
            zip.file(finalName, buf);
        }),
    );

    if (extraTextFiles) {
        for (const [fname, text] of Object.entries(extraTextFiles)) {
            zip.file(sanitizeName(fname), text);
        }
    }

    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
    const a = document.createElement("a");
    const zipName = zipBaseName.endsWith(".zip") ? zipBaseName : `${zipBaseName}.zip`;
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    a.download = sanitizeName(zipName);
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);

    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length) console.warn(`Downloaded with ${failed.length} failure(s).`, failed);
}
